import { type AuthLevel, isAuthorizedToViewContent } from "@/helpers/auth";
import { useHasActiveSubscription } from "@/helpers/subscriptionInfo";
import { findRoutePermission } from "@/routePermissions";
import { findClientRouteAction } from "@/routePermissions";
import { canPerformClientAction } from "@/helpers/clientPermissions";
import { ROUTES } from "@/routes";
import userSelector from "@/store/selectors/userSelector";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

type AuthGuardProps = {
  authLevel: AuthLevel;
};

const AuthGuard = ({ authLevel }: AuthGuardProps) => {
  const navigate = useNavigate();
  const user = useSelector(userSelector);
  const location = useLocation();
  const permission = findRoutePermission(location.pathname);
  const clientAction = findClientRouteAction(location.pathname);
  const hasClientAction = !clientAction || canPerformClientAction(user?.clientRole, clientAction, user?.primaryUser);
  const isAuthorized = isAuthorizedToViewContent(authLevel, user)
    && (!permission || (user && user.privileges?.includes(permission)))
    && (authLevel !== 'client' || hasClientAction);
  const hasActiveSubscription = useHasActiveSubscription();
  const isObligationLink = new URLSearchParams(location.search).has('obligationMunicipalityId');
  const allowedWithoutActiveSubscription: string[] = [
    ROUTES.CLIENT_SUBSCRIPTION,
    ROUTES.CLIENT_CHECKOUT_PAID,
    ROUTES.CLIENT_CHECKOUT_CANCELLED,
    ROUTES.CLIENT_PRICELIST
  ];
  const shouldRedirectToSubscription =
    authLevel === "client" &&
    isAuthorized &&
    !!user?.verifiedEmail &&
    !!user?.verifiedByAdmin &&
    !hasActiveSubscription &&
    !allowedWithoutActiveSubscription.includes(location.pathname);
  
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (authLevel === 'client' && !user && isObligationLink) {
      navigate(ROUTES.LOGIN, {
        replace: true,
        state: { obligationReturnTo: `${location.pathname}${location.search}${location.hash}` },
      });
      return;
    }
    if (!isAuthorized) {
      timeoutId = setTimeout(() => {
        navigate(ROUTES.HOME);
      }, 2500);
    }

    if (authLevel === 'client' && !user?.verifiedEmail && isAuthorized) {
      navigate(ROUTES.VERIFY_EMAIL);
      // @ts-expect-error Not sure why
      return timeoutId && clearTimeout(timeoutId);
    }

    if (authLevel === 'client' && !user?.verifiedByAdmin && isAuthorized) {
      navigate(ROUTES.UNDER_REVIEW);
      // @ts-expect-error Not sure why
      return timeoutId && clearTimeout(timeoutId);
    }

    if (shouldRedirectToSubscription) {
      navigate(ROUTES.CLIENT_SUBSCRIPTION, { replace: true });
      // @ts-expect-error Not sure why
      return timeoutId && clearTimeout(timeoutId);
    }

    return () => timeoutId && clearTimeout(timeoutId);
  }, [navigate, isAuthorized, user, authLevel, shouldRedirectToSubscription, isObligationLink, location.pathname, location.search, location.hash]);

  if (!isAuthorized) {
    return (
      <Stack
        direction={"column"}
        gap={2}
        sx={{ justifyContent: "center", alignItems: "center", height: "75vh", textAlign: "center"}}
      >
        <CircularProgress />
        <Typography variant="h3">Na tuto stránku nemáte oprávnění.</Typography>
        <Typography variant="h4">Přesměrujeme Vás.</Typography>
      </Stack>
    );
  }

  if (authLevel === 'client' && !user?.verifiedEmail) {
    return null;
  }

  if (shouldRedirectToSubscription) {
    return null;
  }

  return <Outlet />;
};

export default AuthGuard;
